import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BarChart3, RefreshCw } from "lucide-react";
import { useScope } from "@/context/ScopeContext";
import { getBackendUrl } from "@/lib/backend";
import { toast } from "sonner";
import "./GerDashboard.css";

const API = `${getBackendUrl()}/api`;
const BAR_MAX = 120;

const Tag = ({ type, children }) => <span className={`tag ${type || ""}`}>{children}</span>;

const GerDashboard = () => {
  const { scope } = useScope();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const buildScopeParams = useCallback(() => {
    const params = {};
    if (scope.districtCode?.trim()) params.district_code = scope.districtCode.trim();
    if (scope.districtName?.trim()) params.district_name = scope.districtName.trim();
    if (scope.blockCode?.trim()) params.block_code = scope.blockCode.trim();
    if (scope.blockName?.trim()) params.block_name = scope.blockName.trim();
    if (scope.udiseCode?.trim()) params.udise_code = scope.udiseCode.trim();
    if (scope.schoolName?.trim()) params.school_name = scope.schoolName.trim();
    return params;
  }, [
    scope.districtCode,
    scope.districtName,
    scope.blockCode,
    scope.blockName,
    scope.udiseCode,
    scope.schoolName,
  ]);

  const fetchData = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await axios.get(`${API}/ger/overview`, { params: buildScopeParams() });
        setData(res.data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load GER dashboard data");
        setData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [buildScopeParams, scope.version],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const gaugeStyle = useMemo(() => {
    const pct = data?.overview?.hero_gauge_pct ?? data?.overview?.hero_ger ?? 0;
    return {
      background: `radial-gradient(circle at center,#fff 0 52%,transparent 53%),conic-gradient(var(--primary) 0 ${pct}%,rgba(37,99,235,.12) ${pct}% 100%)`,
    };
  }, [data?.overview?.hero_gauge_pct, data?.overview?.hero_ger]);

  if (loading) {
    return (
      <div className="content">
        <p style={{ color: "var(--muted)" }}>Loading GER dashboard…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <p style={{ color: "var(--danger)" }}>GER data unavailable. Ensure GER 2025-26.xlsx is in data/excel.</p>
      </div>
    );
  }

  const { meta, overview } = data;

  const scopePill = meta?.scope || "Maharashtra";

  return (
    <div className="content" key={`ger-${scope.version ?? 0}-${meta?.scope_level || "state"}`}>
      <section className="page-header">
        <div>
          <div className="eyebrow">Gross Enrolment Ratio · Official Review</div>
          <h1>GER 2025–26 Analytics</h1>
          <p>
            Separate visual screens for executive review, stage comparison, district ranking, gender equity,
            transition risk, population coverage, intervention heatmap and action tracking.
          </p>
        </div>
        <button type="button" className="primary-action" onClick={() => fetchData(true)} disabled={refreshing || loading}>
          <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {(meta.data_notice || meta.scope_warning) && (
        <div className="scope-notice" role="status">
          {meta.scope_warning || meta.data_notice}
        </div>
      )}

      <div className="top-actions" style={{ justifyContent: "flex-end", display: "flex", gap: 10, marginBottom: 4 }}>
        <span className="filter-chip">
          Year <b>{meta.year}</b>
        </span>
        <span className="filter-chip">
          Scope <b>{meta.scope}</b>
        </span>
        <span className="filter-chip">
          Districts <b>{meta.district_count}</b>
        </span>
      </div>

      {activeTab === "overview" && (
        <section className="view active" id="view-overview">
          <div className="hero-card">
            <div className="hero-layout">
              <div>
                <div className="health-main">
                  <div className="gauge" style={gaugeStyle}>
                    <div>
                      <strong>{overview.hero_ger}%</strong>
                      <span>{overview.hero_stage}</span>
                    </div>
                  </div>
                  <div className="health-copy">
                    <h2>{overview.headline}</h2>
                    <p>{overview.summary}</p>
                    <div className="status-row">
                      {overview.chips?.map((chip) => (
                        <span key={chip} className="status-chip">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="metric-grid">
                {overview.metrics?.map((m) => (
                  <article key={m.label} className="metric-card">
                    <small>{m.label}</small>
                    <strong>{m.value}</strong>
                    <span>{m.hint}</span>
                  </article>
                ))}
              </div>
            </div>
          </div>
          <div className="section-title">
            <div>
              <h2>Stage snapshot</h2>
              <p>Five stage cards make the official review path visible immediately.</p>
            </div>
          </div>
          <div className="domain-grid">
            {overview.stages?.map((stage) => (
              <article key={stage.id} className="domain-card">
                <div className="icon-box">
                  <BarChart3 className="svg-icon" strokeWidth={2} />
                </div>
                <h3>{stage.label}</h3>
                {stage.hint && <small className="stage-hint">{stage.hint}</small>}
                <div className="domain-score">
                  <strong>{stage.ger}%</strong>
                </div>
                <div className="progress-line">
                  <i style={{ width: `${stage.bar_pct}%` }} />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === "stage" && (
        <section className="view active" id="view-stage">
          <div className="analytics-grid">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Stage-wise GER bar view</h3>
                  <p>Clear comparison of GER from primary to higher secondary.</p>
                </div>
                <span className="pill">{scopePill}</span>
              </div>
              {data.stage?.bars?.map((row) => (
                <div key={row.id} className="hbar-row">
                  <div className="hbar-label">{row.label}</div>
                  <div className="hbar-track">
                    <div className="hbar" style={{ width: `${row.bar_pct}%` }} />
                  </div>
                  <div className="hbar-value">{row.ger}%</div>
                </div>
              ))}
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>GER funnel</h3>
                  <p>Grade-band progression from primary to higher secondary (elementary composite excluded).</p>
                </div>
                <span className="pill">Retention proxy</span>
              </div>
              <div className="funnel">
                {data.stage?.funnel?.map((step) => (
                  <div key={step.label} className="funnel-step" style={{ width: `${step.width_pct}%` }}>
                    <span>{step.label}</span>
                    <b>{step.ger}%</b>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "district" && (
        <section className="view active" id="view-district">
          <div className="two-col">
            <article className="panel table-card">
              <div className="table-head">
                <div>
                  <h3>Top districts by Higher Secondary GER</h3>
                  <p>Ranking table for district performance review.</p>
                </div>
                <span className="pill">Top 10</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>District</th>
                    <th>HS GER</th>
                    <th>Secondary</th>
                    <th>Primary</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.district?.top10?.map((row) => (
                    <tr key={row.district} className={row.highlight ? "pune-highlight" : ""}>
                      <td>{row.rank}</td>
                      <td>{row.district}</td>
                      <td className="score">{row.hs_ger}%</td>
                      <td>{row.secondary}%</td>
                      <td>{row.primary}%</td>
                      <td>
                        <Tag type={row.tag}>{row.status}</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
            <article className="panel table-card">
              <div className="table-head">
                <div>
                  <h3>Bottom districts by Higher Secondary GER</h3>
                  <p>Priority districts requiring intervention.</p>
                </div>
                <span className="pill">Bottom 10</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>District</th>
                    <th>HS GER</th>
                    <th>Retention Gap</th>
                    <th>Gender Gap</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.district?.bottom10?.map((row) => (
                    <tr key={row.district}>
                      <td>{row.district}</td>
                      <td className="score">{row.hs_ger}%</td>
                      <td>{row.retention_gap} pts</td>
                      <td>
                        {row.gender_gap >= 0 ? "+" : ""}
                        {row.gender_gap} pts
                      </td>
                      <td>
                        <Tag type={row.tag}>{row.status}</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          </div>
        </section>
      )}

      {activeTab === "gender" && (
        <section className="view active" id="view-gender">
          <div className="section-title">
            <div>
              <h2>Gender Equity</h2>
              <p>Each stage has a separate boys/girls bar pair with Gender Parity Index.</p>
            </div>
          </div>
          <div className="gender-grid">
            {data.gender?.stages?.map((stage) => (
              <div key={stage.id} className="gender-card">
                <div>
                  <b>{stage.label}</b>
                  <span>GPI {stage.gpi}</span>
                </div>
                <div className="mini-bars">
                  <i style={{ width: `${stage.boys / BAR_MAX * 100}%` }} />
                  <em style={{ width: `${stage.girls / BAR_MAX * 100}%` }} />
                </div>
                <small>
                  Boys {stage.boys}% · Girls {stage.girls}%
                </small>
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel-head">
              <div>
                <h3>District gender gap watchlist</h3>
                <p>Higher secondary districts with largest female advantage and male advantage.</p>
              </div>
              <span className="pill">HS stage</span>
            </div>
            <div className="two-col">
              <table>
                <thead>
                  <tr>
                    <th>Female advantage</th>
                    <th>Gap</th>
                    <th>GPI</th>
                  </tr>
                </thead>
                <tbody>
                  {data.gender?.female_advantage?.map((row) => (
                    <tr key={row.district}>
                      <td>{row.district}</td>
                      <td className="score">
                        +{row.gap}
                      </td>
                      <td>{row.gpi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <table>
                <thead>
                  <tr>
                    <th>Male advantage</th>
                    <th>Gap</th>
                    <th>GPI</th>
                  </tr>
                </thead>
                <tbody>
                  {data.gender?.male_advantage?.map((row) => (
                    <tr key={row.district}>
                      <td>{row.district}</td>
                      <td className="score">{row.gap}</td>
                      <td>{row.gpi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {activeTab === "transition" && (
        <section className="view active" id="view-transition">
          <div className="drop-grid">
            {data.transition?.drops?.map((drop) => (
              <div key={drop.label} className="drop-card">
                <span>{drop.label}</span>
                <strong>{drop.drop} pts</strong>
                <div className="drop-track">
                  <i style={{ width: `${drop.bar_pct}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="panel table-card">
            <div className="table-head">
              <div>
                <h3>Largest Primary-to-Higher Secondary retention gaps</h3>
                <p>Districts where coverage drops most sharply by the final school stage.</p>
              </div>
              <span className="pill">Top risk</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>District</th>
                  <th>Primary GER</th>
                  <th>HS GER</th>
                  <th>Retention Gap</th>
                  <th>Action Theme</th>
                </tr>
              </thead>
              <tbody>
                {data.transition?.retention_gaps?.map((row) => (
                  <tr key={row.district} className={row.highlight ? "pune-highlight" : ""}>
                    <td>{row.district}</td>
                    <td>{row.primary}%</td>
                    <td className="score">{row.hs_ger}%</td>
                    <td>{row.retention_gap} pts</td>
                    <td>
                      <Tag type="warn">Transition support</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "population" && (
        <section className="view active" id="view-population">
          <div className="analytics-grid">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Enrolment coverage relationship</h3>
                  <p>Scatter-style view: districts higher and to the right have better continuation from primary to higher secondary.</p>
                </div>
                <span className="pill">Districts</span>
              </div>
              <div className="scatter">
                {data.population?.scatter?.map((pt) => (
                  <span
                    key={pt.district}
                    className={`point${pt.highlight ? " pune-point" : ""}`}
                    style={{ left: `${pt.left_pct}%`, bottom: `${pt.bottom_pct}%` }}
                    title={`${pt.district}: Primary ${pt.primary}, HS ${pt.hs_ger}`}
                  />
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Coverage interpretation</h3>
                  <p>Use this screen to validate whether high early-stage enrolment is translating into higher secondary coverage.</p>
                </div>
              </div>
              <div className="metric-grid">
                {data.population?.coverage?.map((m) => (
                  <article key={m.label} className="metric-card">
                    <small>{m.label}</small>
                    <strong>{m.value}</strong>
                    <span>{m.hint}</span>
                  </article>
                ))}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "risk" && (
        <section className="view active" id="view-risk">
          <div className="panel">
            <div className="panel-head">
              <div>
                <h3>Higher Secondary GER risk heatmap</h3>
                <p>Red cells show districts below 60%, amber below 70%, orange below 80%, green above 80%.</p>
              </div>
              <span className="pill">{meta.district_count} districts</span>
            </div>
            <div className="heat-grid">
              {data.risk?.heatmap?.map((cell) => (
                <div key={cell.district} className={`heat ${cell.risk_class}${cell.highlight ? " pune-highlight" : ""}`}>
                  <b>{cell.district}</b>
                  <span>{cell.hs_ger}%</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === "action" && (
        <section className="view active" id="view-action">
          <div className="action-list">
            {data.action?.themes?.map((item) => (
              <article key={item.title} className="action-item">
                <b>{item.title}</b>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <article className="panel table-card">
            <div className="table-head">
              <div>
                <h3>Priority action tracker</h3>
                <p>Weighted from low HS GER, retention gap and gender gap.</p>
              </div>
              <span className="pill">Top 12</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>District</th>
                  <th>Score</th>
                  <th>HS GER</th>
                  <th>Retention Gap</th>
                  <th>Intervention</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {data.action?.tracker?.map((row) => (
                  <tr key={row.district}>
                    <td>{row.priority}</td>
                    <td>{row.district}</td>
                    <td>{row.score}</td>
                    <td>{row.hs_ger}%</td>
                    <td>{row.retention_gap}</td>
                    <td>{row.intervention}</td>
                    <td>
                      <Tag type="risk">30 days</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        </section>
      )}
    </div>
  );
};

export default GerDashboard;
